// Shared Excel-grid building blocks (#33) — extracted from the identical
// copies that had been pasted into GSTPage and TDSPage. Import from here so
// grid fixes apply once, not per page.
//
// NOTE: IncomeTaxPage still has its own older variants of these with
// different prop APIs (x/y-positioned filter, data-driven unique values)
// plus ITR-specific behavior; consolidating it onto these components is a
// follow-up that needs its call sites reworked.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Filter } from 'lucide-react';

// ── useExcelNav ────────────────────────────────────────────────────────────────
// Keyboard navigation + active/editing cell state for Excel-style grids:
// arrows/Tab move, Enter or F2 edits, Esc deselects, Space toggles row select.
// Scroll-into-view runs on every active-cell change (instant — grids are
// animation-free) and targets the data-cell attribute EditableCell renders.
export function useExcelNav({ rows, cols, readonlyCols = new Set(), canEdit = true, onToggleSelect }) {
  const containerRef = useRef();
  const [activeCell, setActiveCell] = useState(null);
  const [editingCell, setEditingCell] = useState(null);

  useEffect(() => {
    if (!activeCell) return;
    const el = containerRef.current?.querySelector(`[data-cell="${activeCell.rowId}-${activeCell.col}"]`);
    el?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeCell]);

  const activate = useCallback((rowId, col) => {
    setActiveCell({ rowId, col });
    setEditingCell(null);
    containerRef.current?.focus();
  }, []);
  const startEdit = useCallback((rowId, col) => {
    if (!canEdit || readonlyCols.has(col)) return;
    setActiveCell({ rowId, col });
    setEditingCell({ rowId, col });
  }, [canEdit, readonlyCols]);
  const cancelEdit = useCallback(() => {
    setEditingCell(null);
    containerRef.current?.focus();
  }, []);
  // After a commit: keep the cell active, or step down a row (Enter-commit)
  const settleAfterCommit = useCallback((rowId, col, moveDown) => {
    setEditingCell(null);
    if (moveDown) {
      const idx = rows.findIndex(r => r.id === rowId);
      setActiveCell({ rowId: (rows[idx + 1] || rows[idx])?.id ?? rowId, col });
    } else {
      setActiveCell({ rowId, col });
    }
    containerRef.current?.focus();
  }, [rows]);

  const handleKeyDown = e => {
    if (!activeCell) return;
    if (editingCell) {
      if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
      return;
    }
    const { rowId, col } = activeCell;
    const rowIdx = rows.findIndex(r => r.id === rowId);
    const colIdx = cols.indexOf(col);
    if (rowIdx === -1 || colIdx === -1) return;
    const move = (ri, ci) => setActiveCell({ rowId: rows[ri].id, col: cols[ci] });
    switch (e.key) {
      case 'ArrowRight': e.preventDefault();
        if (colIdx < cols.length - 1) move(rowIdx, colIdx + 1);
        else if (rowIdx < rows.length - 1) move(rowIdx + 1, 0);
        break;
      case 'ArrowLeft': e.preventDefault();
        if (colIdx > 0) move(rowIdx, colIdx - 1);
        else if (rowIdx > 0) move(rowIdx - 1, cols.length - 1);
        break;
      case 'ArrowDown': e.preventDefault();
        if (rowIdx < rows.length - 1) move(rowIdx + 1, colIdx);
        break;
      case 'ArrowUp': e.preventDefault();
        if (rowIdx > 0) move(rowIdx - 1, colIdx);
        break;
      case 'Tab': e.preventDefault();
        if (e.shiftKey) {
          if (colIdx > 0) move(rowIdx, colIdx - 1);
          else if (rowIdx > 0) move(rowIdx - 1, cols.length - 1);
        } else {
          if (colIdx < cols.length - 1) move(rowIdx, colIdx + 1);
          else if (rowIdx < rows.length - 1) move(rowIdx + 1, 0);
        }
        break;
      case 'Enter': case 'F2': e.preventDefault(); startEdit(rowId, col); break;
      case 'Escape': setActiveCell(null); break;
      case ' ': e.preventDefault(); onToggleSelect?.(rowId); break;
      default: break;
    }
  };

  const isCellActive = (rowId, col) => activeCell?.rowId === rowId && activeCell?.col === col;
  const isCellEditing = (rowId, col) => editingCell?.rowId === rowId && editingCell?.col === col;

  return { containerRef, activeCell, editingCell, activate, startEdit, cancelEdit, settleAfterCommit, handleKeyDown, isCellActive, isCellEditing };
}

// ── ColFilterDropdown ──────────────────────────────────────────────────────────
// dateCols: Set of column keys that use a from/to date range instead of a
// checkbox list (was a hidden per-page DATE_COLS dependency before extraction)
export function ColFilterDropdown({ col, uniqueValues, current, onApply, onClose, anchorRef, dateCols }) {
  const isDate = dateCols?.has(col) || col === 'effective_due_date';
  const [sel, setSel] = useState(Array.isArray(current) ? current : []);
  const [from, setFrom] = useState(current?.from || '');
  const [to, setTo] = useState(current?.to || '');
  const ref = useRef();

  useEffect(() => {
    const handleClick = e => { if (!ref.current?.contains(e.target) && !anchorRef?.current?.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose, anchorRef]);

  const apply = () => {
    if (isDate) onApply(from || to ? { from, to } : null);
    else onApply(sel.length ? sel : null);
  };

  const rect = anchorRef?.current?.getBoundingClientRect();
  const style = rect ? { position: 'fixed', top: rect.bottom + 4, left: rect.left, zIndex: 9999, minWidth: 180 } : {};

  return (
    <div ref={ref} style={style} className="bg-white border border-gray-200 rounded-xl shadow-xl p-3">
      {isDate ? (
        <div className="space-y-2">
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} placeholder="From" className="input-field text-xs w-full" />
          <input type="date" value={to} onChange={e => setTo(e.target.value)} placeholder="To" className="input-field text-xs w-full" />
        </div>
      ) : (
        <div className="space-y-1 max-h-44 overflow-y-auto">
          {uniqueValues.map(v => (
            <label key={v} className="flex items-center gap-2 px-1 py-0.5 rounded hover:bg-gray-50 cursor-pointer text-xs">
              <input type="checkbox" checked={sel.includes(v)} onChange={() => setSel(s => s.includes(v) ? s.filter(x => x !== v) : [...s, v])} className="rounded" />
              <span className="text-gray-700">{v || '(blank)'}</span>
            </label>
          ))}
        </div>
      )}
      <div className="flex gap-2 mt-2 pt-2 border-t border-gray-100">
        <button onClick={apply} className="flex-1 text-xs bg-[#1e3a5f] text-white rounded-lg py-1.5 font-medium">Apply</button>
        <button onClick={() => { setSel([]); setFrom(''); setTo(''); onApply(null); }} className="flex-1 text-xs border border-gray-200 rounded-lg py-1.5 text-gray-600">Clear</button>
      </div>
    </div>
  );
}

// ── FilterTh ──────────────────────────────────────────────────────────────────
export function FilterTh({ col, label, sortKey, sortDir, onSort, colFilters, openColFilter }) {
  const ref = useRef();
  const active = colFilters[col] && (Array.isArray(colFilters[col]) ? colFilters[col].length > 0 : !!(colFilters[col]?.from || colFilters[col]?.to));
  return (
    <th ref={ref} className="table-header cursor-pointer select-none whitespace-nowrap">
      <div className="flex items-center gap-1" onClick={() => onSort(col)}>
        <span>{label}</span>
        {sortKey === col && <span className="text-[#f0a500]">{sortDir === 'asc' ? '↑' : '↓'}</span>}
        <button onClick={e => { e.stopPropagation(); openColFilter(col, ref); }}
          className={`ml-0.5 p-0.5 rounded ${active ? 'text-[#f0a500]' : 'text-gray-300 hover:text-gray-500'}`}>
          <Filter size={10} />
        </button>
      </div>
    </th>
  );
}

// ── InlineDropdown ─────────────────────────────────────────────────────────────
export function InlineDropdown({ options, value, onCommit, onCancel }) {
  const [idx, setIdx] = useState(() => Math.max(0, options.findIndex(o => (o.value !== undefined ? o.value : o) === value)));
  const ref = useRef();
  useEffect(() => { ref.current?.focus(); ref.current?.children[idx]?.scrollIntoView({ block: 'nearest' }); }, []);
  const handleKey = e => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setIdx(i => Math.min(i + 1, options.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); const o = options[idx]; onCommit(o.value !== undefined ? o.value : o); }
    else if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
  };
  return (
    <div ref={ref} tabIndex={0} onKeyDown={handleKey}
      className="absolute z-40 bg-white border border-[#1e3a5f]/30 rounded-lg shadow-lg overflow-y-auto max-h-44 min-w-[140px] py-1 outline-none"
      style={{ top: '100%', left: 0 }}>
      {options.map((o, i) => {
        const val = o.value !== undefined ? o.value : o; const lbl = o.label !== undefined ? o.label : o;
        return (
          <div key={String(val)} onMouseDown={() => onCommit(val)}
            className={`px-3 py-1.5 text-xs cursor-pointer ${i === idx ? 'bg-[#1e3a5f] text-white' : 'hover:bg-gray-100 text-gray-700'}`}>
            {lbl}
          </div>
        );
      })}
    </div>
  );
}

// ── patchRows ──────────────────────────────────────────────────────────────────
// THE one "instant update" for grid saves (inline + bulk): apply a partial
// update to the matching rows in local state and re-derive the *_name display
// fields from their id fields, so the grid reflects a successful save without
// a refetch. Grids display joined names (primary_assignee_name / staff_name);
// saves send ids — patching only the id leaves the visible name stale.
export function patchRows(rows, ids, updates, { staff = [] } = {}) {
  const idSet = new Set((Array.isArray(ids) ? ids : [ids]).map(Number));
  const staffName = id => staff.find(s => String(s.id) === String(id))?.name || null;
  return rows.map(r => {
    if (!idSet.has(Number(r.id))) return r;
    const next = { ...r, ...updates };
    if ('primary_assignee_id' in updates) next.primary_assignee_name = updates.primary_assignee_id ? staffName(updates.primary_assignee_id) : null;
    if ('assigned_staff_id' in updates) next.staff_name = updates.assigned_staff_id ? staffName(updates.assigned_staff_id) : null;
    return next;
  });
}

// ── EditableCell ───────────────────────────────────────────────────────────────
// onCommit(col, val, trigger) — trigger is 'enter' | 'blur' | 'select' so pages
// can implement Enter-commit-moves-down (extra arg is ignored by older callers).
// Unchanged text/date values cancel instead of committing (no pointless writes —
// every sql.js write persists the whole DB file).
export function EditableCell({ row, col, displayValue, editOptions, isActive, isEditing, onActivate, onStartEdit, onCommit, onCancel }) {
  const [flash, setFlash] = useState(null);
  const commit = async (val, trigger) => {
    if (editOptions?.type !== 'select' && String(val ?? '') === String(row[col] ?? '')) { onCancel(); return; }
    try { await onCommit(col, val, trigger); setFlash('ok'); setTimeout(() => setFlash(null), 600); }
    catch { setFlash('err'); setTimeout(() => setFlash(null), 600); }
  };
  const cellCls = `table-cell relative cursor-pointer outline-none
    ${isActive ? 'ring-2 ring-inset ring-[#1e3a5f]/30 bg-blue-50/20' : ''}
    ${flash === 'ok' ? 'bg-emerald-100' : flash === 'err' ? 'bg-red-100' : ''}`;
  return (
    <td data-cell={`${row.id}-${col}`} className={cellCls} tabIndex={0}
      onClick={() => { onActivate(); if (editOptions) onStartEdit(); }}>
      {isEditing && editOptions ? (
        editOptions.type === 'select' ? (
          <div className="relative">
            {displayValue}
            <InlineDropdown options={editOptions.options} value={String(row[col] ?? '')} onCommit={v => commit(v, 'select')} onCancel={onCancel} />
          </div>
        ) : editOptions.type === 'date' ? (
          <input type="date" defaultValue={row[col] || ''} autoFocus className="w-full text-xs bg-transparent border-0 outline-none p-0"
            onBlur={e => commit(e.target.value, 'blur')}
            onKeyDown={e => { if (e.key === 'Enter') commit(e.target.value, 'enter'); if (e.key === 'Escape') onCancel(); }} />
        ) : (
          <input type="text" defaultValue={row[col] || ''} autoFocus className="w-full text-xs bg-transparent border-0 outline-none p-0"
            onBlur={e => commit(e.target.value, 'blur')}
            onKeyDown={e => { if (e.key === 'Enter') commit(e.target.value, 'enter'); if (e.key === 'Escape') onCancel(); }} />
        )
      ) : displayValue}
    </td>
  );
}
